<template>
  <div class="share-wrap" :class="{ compact: compact }">
    <button
      v-if="compact"
      type="button"
      class="share-overflow-btn"
      :aria-expanded="menuOpen ? 'true' : 'false'"
      aria-haspopup="true"
      @click="menuOpen = !menuOpen"
    >
      <i class="fas fa-share-alt" aria-hidden="true"></i>
      Share
      <i :class="menuOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" aria-hidden="true"></i>
    </button>

    <div
      v-show="!compact || menuOpen"
      class="share-buttons"
      :class="{ 'share-buttons--menu': compact }"
      role="group"
      aria-label="Share and print"
    >
      <button type="button" @click="shareOnFacebook" class="share-btn">
        <i class="fab fa-facebook-f" aria-hidden="true"></i>
        <span>Facebook</span>
      </button>
      <button type="button" @click="shareOnTwitter" class="share-btn">
        <i class="fab fa-twitter" aria-hidden="true"></i>
        <span>Twitter</span>
      </button>
      <button type="button" @click="shareOnLinkedIn" class="share-btn">
        <i class="fab fa-linkedin-in" aria-hidden="true"></i>
        <span>LinkedIn</span>
      </button>
      <button type="button" @click="copyLink" class="share-btn">
        <i class="fas fa-link" aria-hidden="true"></i>
        <span>Copy Link</span>
      </button>
      <button type="button" @click="printPage" class="share-btn">
        <i class="fas fa-print" aria-hidden="true"></i>
        <span>Print</span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ShareButtons',
  props: {
    url: {
      type: String,
      default: () => (typeof window !== 'undefined' ? window.location.href : '')
    },
    title: {
      type: String,
      default: 'Check out this cruise!'
    },
    description: {
      type: String,
      default: ''
    },
    /** CP-32: bury Share/Print behind overflow so Book Now dominates */
    compact: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return { menuOpen: false };
  },
  methods: {
    shareOnFacebook() {
      const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.url)}`;
      window.open(shareUrl, '_blank', 'width=600,height=400');
    },
    shareOnTwitter() {
      const text = `${this.title} ${this.description}`.trim();
      const shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(this.url)}&text=${encodeURIComponent(text)}`;
      window.open(shareUrl, '_blank', 'width=600,height=400');
    },
    shareOnLinkedIn() {
      const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(this.url)}`;
      window.open(shareUrl, '_blank', 'width=600,height=400');
    },
    async copyLink(event) {
      try {
        await navigator.clipboard.writeText(this.url);
        const btn = event.currentTarget;
        const span = btn.querySelector('span');
        const originalText = span.textContent;
        span.textContent = 'Copied!';
        setTimeout(() => { span.textContent = originalText; }, 2000);
      } catch (err) {
        console.error('Failed to copy link:', err);
        alert('Failed to copy link. Please copy manually: ' + this.url);
      }
    },
    printPage() {
      window.print();
    }
  }
};
</script>

<style scoped>
.share-wrap {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.share-overflow-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-height: 36px;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--cs-card-border);
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--cs-muted);
  font-size: var(--text-small);
  font-weight: 600;
  cursor: pointer;
  width: 100%;
}

.share-overflow-btn:hover {
  background: var(--cs-signal-mist);
  color: var(--cs-signal-teal);
}

.share-overflow-btn:focus-visible {
  outline: 2px solid var(--cs-signal-teal);
  outline-offset: 2px;
}

.share-buttons {
  display: flex;
  gap: var(--space-2);
  flex-wrap: wrap;
}

.share-buttons--menu {
  flex-direction: column;
  gap: var(--space-1);
}

.share-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-height: var(--touch-min);
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--cs-card-border);
  border-radius: var(--radius-md);
  background-color: var(--cs-card);
  color: var(--cs-muted);
  font-size: var(--text-small);
  cursor: pointer;
  transition: background var(--transition-fast), border-color var(--transition-fast), color var(--transition-fast);
}

.share-buttons--menu .share-btn {
  width: 100%;
  justify-content: flex-start;
  min-height: 40px;
}

.share-btn:hover {
  background-color: var(--cs-signal-mist);
  border-color: rgba(11, 91, 99, 0.25);
  color: var(--cs-signal-teal);
}

.share-btn:focus-visible {
  outline: 2px solid var(--cs-signal-teal);
  outline-offset: 2px;
}

/* Non-compact (legacy hero) — keep readable on light surfaces */
.share-wrap:not(.compact) .share-btn {
  border-color: rgba(255, 255, 255, 0.45);
  background-color: rgba(232, 243, 244, 0.2);
  color: inherit;
}

.share-wrap:not(.compact) .share-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.7);
}

@media (max-width: 640px) {
  .share-wrap:not(.compact) .share-btn span {
    display: none;
  }
  .share-wrap:not(.compact) .share-btn {
    width: var(--touch-min);
    padding: 0;
  }
}

@media print {
  .share-wrap { display: none; }
}
</style>
